import { useState, useCallback, useRef, useEffect } from 'react';
import { useSidebar } from '@/context/SidebarContext';
import { useToast } from '@/hooks/use-toast';
import { SaveCalculationDialog } from './SaveCalculationDialog';
import { SidebarHeader } from './components/SidebarHeader';
import { cn } from '@/lib/utils';
import { EarlyWarningData, SavedEarlyWarning } from '@/types/early-warning';
import { saveEarlyWarning } from '@/lib/supabase/early-warning';
import { useCalculations } from '@/hooks/useCalculations';
import { Loader2, AlertCircle, FileText, Calendar } from 'lucide-react';

interface EarlyWarningSidebarProps {
  data: EarlyWarningData | null;
  onLoad: (calculation: SavedEarlyWarning) => void;
  className?: string;
}

// Enhanced SidebarContent component with improved collapsed view
const EnhancedSidebarContent = ({
  calculations,
  isLoading,
  error,
  onLoad,
  activeCalculationId,
  isMinimized,
}: {
  calculations: SavedEarlyWarning[];
  isLoading: boolean;
  error: Error | null;
  onLoad: (calculation: SavedEarlyWarning) => void;
  activeCalculationId: string | null;
  isMinimized: boolean;
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
        <p className="text-sm text-gray-600">{error.message}</p>
      </div>
    );
  }

  if (!calculations || calculations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <FileText className="h-8 w-8 text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">No saved calculations</p>
      </div>
    );
  }

  // Group calculations by month for better organization
  const groupedCalculations = calculations.reduce((acc, calc) => {
    const month = new Date(calc.date_saved).toLocaleString('default', { month: 'short', year: 'numeric' });
    if (!acc[month]) acc[month] = [];
    acc[month].push(calc);
    return acc;
  }, {} as Record<string, SavedEarlyWarning[]>);

  // Get warning count and risk level for visual indicators
  const getWarningInfo = (calculation: SavedEarlyWarning) => {
    const warnings = calculation.data?.warnings || [];
    const warningCount = warnings.length;
    
    // Check for overdue items
    const hasOverdue = warnings.some(w => w.status === 'OVERDUE');
    
    // Check for high risk items
    const hasHighRisk = warnings.some(w => w.score >= 4);
    
    let statusColor = 'bg-gray-400'; // Default
    if (hasOverdue) statusColor = 'bg-red-500';
    else if (hasHighRisk) statusColor = 'bg-amber-500';
    else if (warningCount > 0) statusColor = 'bg-blue-500';

    return { warningCount, statusColor };
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {Object.entries(groupedCalculations).map(([month, monthCalculations]) => (
        <div key={month} className="mb-2">
          {!isMinimized && (
            <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">
              {month}
            </div>
          )}
          <div className={isMinimized ? "px-2" : ""}>
            {monthCalculations.map((calculation) => {
              const { warningCount, statusColor } = getWarningInfo(calculation);
              const initials = calculation.project_name
                .split(' ')
                .map(word => word[0])
                .slice(0, 2)
                .join('')
                .toUpperCase();

              const isActive = calculation.id === activeCalculationId;

              return (
                <div
                  key={calculation.id}
                  className={cn(
                    'group rounded-md transition-all duration-300 cursor-pointer',
                    isActive ? 'bg-blue-50' : 'hover:bg-gray-50',
                    isMinimized ? 'py-3 px-2' : 'p-3 mx-2 mb-1'
                  )}
                  onClick={() => onLoad(calculation)}
                  title={`${calculation.project_name} - Last saved: ${new Date(calculation.date_saved).toLocaleString()}`}
                >
                  {isMinimized ? (
                    <div className="flex flex-col items-center">
                      {/* Compact avatar with status indicator */}
                      <div className="relative mb-1">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm",
                          isActive ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"
                        )}>
                          {initials}
                        </div>
                        {/* Status indicator */}
                        <div className={cn(
                          "absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white",
                          statusColor
                        )} />
                        
                        {/* Warning count badge */}
                        {warningCount > 0 && (
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border border-gray-200 text-xs flex items-center justify-center">
                            {warningCount > 99 ? '99+' : warningCount}
                          </div>
                        )}
                      </div>
                      
                      {/* Abbreviated name */}
                      <span className="text-xs text-center font-medium truncate w-full">
                        {calculation.project_name.slice(0, 6)}{calculation.project_name.length > 6 ? '...' : ''}
                      </span>
                      
                      {/* Last saved date - compact format */}
                      <span className="text-[10px] text-gray-400">
                        {new Date(calculation.date_saved).toLocaleDateString(undefined, { 
                          month: 'numeric', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-start">
                      {/* Avatar for expanded view */}
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center font-medium flex-shrink-0",
                        isActive ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"
                      )}>
                        {initials}
                      </div>
                      
                      <div className="ml-3 flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <h3 className="text-sm font-medium truncate">
                            {calculation.project_name}
                          </h3>
                          <div className={cn(
                            "w-2 h-2 rounded-full mt-1",
                            statusColor
                          )} />
                        </div>
                        
                        <div className="flex items-center mt-1 text-xs text-gray-500">
                          <Calendar className="w-3 h-3 mr-1" />
                          <span>
                            {new Date(calculation.date_saved).toLocaleDateString()}
                          </span>
                        </div>
                        
                        <div className="mt-2 flex justify-between items-center">
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                            {warningCount} warning{warningCount !== 1 ? 's' : ''}
                          </span>
                          
                          {/* Progress bar visualization */}
                          <div className="relative w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className={statusColor}
                              style={{ width: `${Math.min(100, warningCount * 10)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export function EarlyWarningSidebar({ data, onLoad, className }: EarlyWarningSidebarProps) {
  const { isCollapsed, toggleCollapsed } = useSidebar();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSaveDialogOpen, setSaveDialogOpen] = useState(false);
  const [temporarilyExpanded, setTemporarilyExpanded] = useState(false);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();
  const {
    calculations,
    isLoading,
    error,
    activeCalculationId,
    setActiveCalculationId,
    refreshCalculations,
  } = useCalculations('early_warning_register' as const);

  // Handle hover expansion
  const handleMouseEnter = useCallback(() => {
    if (isCollapsed) {
      hoverTimeout.current = setTimeout(() => {
        setTemporarilyExpanded(true);
      }, 800); // 800ms delay before auto-expanding
    }
  }, [isCollapsed]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setTemporarilyExpanded(false);
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout.current) {
        clearTimeout(hoverTimeout.current);
      }
    };
  }, []);

  const handleSave = useCallback(async (name: string, warningData: EarlyWarningData) => {
    try {
      // Validate data before saving
      if (!warningData || typeof warningData !== 'object') {
        throw new Error('No valid warning data to save');
      }
      
      // Create a clean object to ensure we're not passing anything unexpected
      const cleanData: EarlyWarningData = {
        projectName: warningData.projectName || name,
        clientName: warningData.clientName || '',
        projectStartDate: warningData.projectStartDate || '',
        projectEndDate: warningData.projectEndDate || '',
        warnings: warningData.warnings || [],
        ...warningData
      };
      
      await saveEarlyWarning(name, cleanData);
      toast({
        title: 'Success',
        description: 'Early warning saved successfully',
      });
      setSaveDialogOpen(false);
      
      // Refresh calculations after save
      setTimeout(() => {
        refreshCalculations();
      }, 500);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save early warning',
        variant: 'destructive',
      });
    }
  }, [toast, refreshCalculations]);

  const handleOpenSaveDialog = useCallback(() => {
    if (!data) {
      toast({
        title: 'Error',
        description: 'No warning data to save',
        variant: 'destructive',
      });
      return;
    }
    setSaveDialogOpen(true);
  }, [data, toast]);

  const filteredCalculations = useCallback(() => {
    if (!calculations || !Array.isArray(calculations)) {
      return [];
    }
    
    if (!searchQuery.trim()) return calculations;
    
    const query = searchQuery.toLowerCase();
    return calculations.filter((calc) => {
      return calc && typeof calc === 'object' && calc.project_name && 
        calc.project_name.toLowerCase().includes(query);
    });
  }, [calculations, searchQuery]);

  const handleLoadCalculation = useCallback((calculation: SavedEarlyWarning) => {
    try {
      onLoad(calculation);
      setActiveCalculationId(calculation.id);
      // Close sidebar on mobile after selection
      if (window.innerWidth < 768) {
        setTemporarilyExpanded(false);
      }
    } catch (error) {
      console.error("Error loading calculation:", error);
      toast({
        title: 'Error',
        description: 'Failed to load calculation',
        variant: 'destructive',
      });
    }
  }, [onLoad, setActiveCalculationId, toast]);

  // Apply temporary expansion
  const effectivelyCollapsed = isCollapsed && !temporarilyExpanded;

  return (
    <div 
      className={cn(
        'border-r bg-background flex flex-col transition-all duration-300',
        effectivelyCollapsed ? 'w-20' : 'w-80',
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SidebarHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSaveClick={handleOpenSaveDialog}
        onToggleCollapse={toggleCollapsed}
        isMinimized={effectivelyCollapsed}
      />
      <EnhancedSidebarContent
        calculations={filteredCalculations()}
        isLoading={isLoading}
        error={error}
        onLoad={handleLoadCalculation}
        activeCalculationId={activeCalculationId}
        isMinimized={effectivelyCollapsed}
      />
      <SaveCalculationDialog
        open={isSaveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        data={data}
        onSave={handleSave}
      />
    </div>
  );
}